<template>
  <div class="modal fade" id="route-selector-dialog" tabindex="-1" role="dialog" aria-hidden="true">
    <div class="modal-dialog modal-md modal-dialog-centered" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title">{{ hikeName }}</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close"></button>
        </div>
        <div class="modal-body p-0">
          <div class="route-selector-container">
            <div class="selector-header">
              <div class="d-flex justify-content-between align-items-center">
                <p class="mb-0">Select routes to import:</p>
                <button
                  type="button"
                  class="btn btn-sm btn-outline-light select-all-btn"
                  @click="toggleSelectAll"
                >
                  {{ allSelected ? 'Deselect All' : 'Select All' }}
                </button>
              </div>
            </div>

            <div class="trail-list">
              <!-- Show days if there are multiple trails with day info -->
              <template v-if="showDays">
                <div v-for="day in daysToShow" :key="`day-${day}`" class="day-section">
                  <div class="day-header">
                    <label :for="`day-checkbox-${day}`" class="day-label">
                      <span class="day-title">
                        {{ getDayName(day) }}
                        <small v-if="getDayDescription(day)" class="day-description">{{ getDayDescription(day) }}</small>
                      </span>
                    </label>
                    <input
                      type="checkbox"
                      class="day-checkbox"
                      :id="`day-checkbox-${day}`"
                      :checked="isDayFullySelected(day)"
                      :indeterminate.prop="isDayPartiallySelected(day)"
                      @change="toggleDay(day)"
                    />
                  </div>
                  <div
                    v-for="trail in getTrailsForDay(day)"
                    :key="getTrailId(trail)"
                    class="trail-item"
                    :class="{ selected: selectedRouteIds.includes(getTrailId(trail)) }"
                  >
                    <label :for="`route-checkbox-${getTrailId(trail)}`" class="trail-info">
                      <h4 class="trail-title">{{ getTrailTitle(trail) }}</h4>
                      <div class="trail-stats">
                        <span>{{ formatDistance(getTrailDistance(trail)) }}</span>
                        <span class="separator">·</span>
                        <span>{{ formatElevation(getTrailElevation(trail)) }}</span>
                        <span class="separator">·</span>
                        <span>{{ formatDuration(getTrailDuration(trail)) }}</span>
                      </div>
                    </label>
                    <input
                      type="checkbox"
                      class="trail-checkbox"
                      :id="`route-checkbox-${getTrailId(trail)}`"
                      :value="getTrailId(trail)"
                      v-model="selectedRouteIds"
                    />
                  </div>
                </div>
              </template>

              <!-- Show simple list if no day info -->
              <template v-else>
                <div
                  v-for="(trail, index) in sortedTrails"
                  :key="getTrailId(trail)"
                  class="trail-item"
                  :class="{ selected: selectedRouteIds.includes(getTrailId(trail)) }"
                >
                  <input
                    type="checkbox"
                    class="trail-checkbox"
                    :id="`route-checkbox-${getTrailId(trail)}`"
                    :value="getTrailId(trail)"
                    v-model="selectedRouteIds"
                  />
                  <div class="trail-number">{{ index + 1 }}</div>
                  <label :for="`route-checkbox-${getTrailId(trail)}`" class="trail-info">
                    <h4 class="trail-title">{{ getTrailTitle(trail) }}</h4>
                    <div class="trail-stats">
                      <span>{{ formatDistance(getTrailDistance(trail)) }}</span>
                      <span class="separator">·</span>
                      <span>{{ formatElevation(getTrailElevation(trail)) }}</span>
                      <span class="separator">·</span>
                      <span>{{ formatDuration(getTrailDuration(trail)) }}</span>
                    </div>
                  </label>
                </div>
              </template>
            </div>

            <div class="panel-footer" v-if="selectedRouteIds.length > 0">
              <div class="total-stats">
                <div class="total-label">Selected {{ selectedRouteIds.length }} route{{ selectedRouteIds.length !== 1 ? 's' : '' }}</div>
                <div class="total-values">
                  <strong>{{ formatDistance(totalSelectedDistance) }}</strong>
                  <span class="separator">·</span>
                  <strong>{{ formatElevation(totalSelectedElevation) }}</strong>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal">Cancel</button>
          <button
            type="button"
            class="btn btn-primary"
            :disabled="selectedRouteIds.length === 0"
            @click="importSelected"
          >
            Import {{ selectedRouteIds.length || '' }} Route{{ selectedRouteIds.length !== 1 ? 's' : '' }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { defineComponent } from 'vue';
import { MeasurementPrefUtility } from '../../utils/helpers/MeasurementPref.ts';

export default defineComponent({
  name: 'RouteSelector',
  data() {
    return {
      hike: null,
      hikeName: '',
      hikeDays: [],
      plannedTrails: [],
      selectedRouteIds: [],
      callback: null,
    };
  },
  computed: {
    hasJoinTableData() {
      if (this.plannedTrails.length === 0) return false;
      const firstTrail = this.plannedTrails[0];
      return firstTrail && 'day' in firstTrail && 'ptv2PlannedTrail' in firstTrail;
    },

    showDays() {
      if (!this.hasJoinTableData) return false;
      return this.plannedTrails.length > 1 && this.plannedTrails.some((t) => t.day != null);
    },

    sortedTrails() {
      const trails = [...this.plannedTrails];
      return trails.sort((a, b) => {
        const posA = this.hasJoinTableData ? (a.position || 0) : this.plannedTrails.indexOf(a);
        const posB = this.hasJoinTableData ? (b.position || 0) : this.plannedTrails.indexOf(b);
        return posA - posB;
      });
    },

    daysToShow() {
      if (!this.showDays) return [];
      const days = new Set();
      this.plannedTrails.forEach((trail) => {
        if (trail.day != null) {
          days.add(trail.day);
        }
      });
      return Array.from(days).sort((a, b) => a - b);
    },

    totalSelectedDistance() {
      return this.getSelectedTrails().reduce((sum, trail) => {
        return sum + this.getTrailDistance(trail);
      }, 0);
    },

    totalSelectedElevation() {
      return this.getSelectedTrails().reduce((sum, trail) => {
        return sum + this.getTrailElevation(trail);
      }, 0);
    },

    allSelected() {
      if (this.sortedTrails.length === 0) return false;
      return this.sortedTrails.every((trail) =>
        this.selectedRouteIds.includes(this.getTrailId(trail))
      );
    },
  },
  methods: {
    show(hike, callback) {
      this.hike = hike;
      this.hikeName = hike.name;
      this.hikeDays = hike.hikeDays || [];

      // Filter out empty routes (routes without lineStrings)
      const allTrails = hike.hikesPlannedTrails || [];
      this.plannedTrails = allTrails.filter((trail) => {
        const plannedTrail = trail.ptv2PlannedTrail;
        // Keep routes that have lineStrings
        return plannedTrail && plannedTrail.lineStrings && plannedTrail.lineStrings.length > 0;
      });

      this.selectedRouteIds = [];
      this.callback = callback;
    },

    getTrailId(trail) {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.id || trail.id;
      }
      return trail.id;
    },

    getTrailTitle(trail) {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.title || `Route ${trail.position}`;
      }
      return trail.title || `Route ${this.plannedTrails.indexOf(trail) + 1}`;
    },

    getTrailDistance(trail) {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.distanceMeters || 0;
      }
      return trail.distanceMeters || 0;
    },

    getTrailElevation(trail) {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.elevationGainMeters || 0;
      }
      return trail.elevationGainMeters || 0;
    },

    getTrailDuration(trail) {
      if (this.hasJoinTableData) {
        return trail.ptv2PlannedTrail?.calculatedDurationMinutes || 0;
      }
      return trail.calculatedDurationMinutes || 0;
    },

    getTrailsForDay(day) {
      return this.sortedTrails.filter((trail) => trail.day === day);
    },

    getDayName(dayNumber) {
      const dayInfo = this.hikeDays.find((d) => d.dayNumber === dayNumber);
      return dayInfo?.name || `Day ${dayNumber}`;
    },

    getDayDescription(dayNumber) {
      const dayInfo = this.hikeDays.find((d) => d.dayNumber === dayNumber);
      return dayInfo?.description || '';
    },

    formatDistance(distanceMeters) {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.bigUnitFormatter(distanceMeters / 1000, true);
    },

    formatElevation(elevationMeters) {
      const measurementPref = MeasurementPrefUtility.preference();
      return measurementPref.smallUnitFormatter(elevationMeters, false);
    },

    formatDuration(minutes) {
      if (!minutes) return '0h';
      const hours = Math.floor(minutes / 60);
      const mins = minutes % 60;
      if (hours === 0) return `${mins}m`;
      if (mins === 0) return `${hours}h`;
      return `${hours}h ${mins}m`;
    },

    isDayFullySelected(day) {
      const dayTrails = this.getTrailsForDay(day);
      if (dayTrails.length === 0) return false;
      return dayTrails.every((trail) => this.selectedRouteIds.includes(this.getTrailId(trail)));
    },

    isDayPartiallySelected(day) {
      const dayTrails = this.getTrailsForDay(day);
      if (dayTrails.length === 0) return false;
      const selectedCount = dayTrails.filter((trail) =>
        this.selectedRouteIds.includes(this.getTrailId(trail))
      ).length;
      return selectedCount > 0 && selectedCount < dayTrails.length;
    },

    toggleDay(day) {
      const dayTrails = this.getTrailsForDay(day);
      const allSelected = this.isDayFullySelected(day);

      if (allSelected) {
        // Deselect all routes in this day
        dayTrails.forEach((trail) => {
          const id = this.getTrailId(trail);
          const index = this.selectedRouteIds.indexOf(id);
          if (index > -1) {
            this.selectedRouteIds.splice(index, 1);
          }
        });
      } else {
        // Select all routes in this day
        dayTrails.forEach((trail) => {
          const id = this.getTrailId(trail);
          if (!this.selectedRouteIds.includes(id)) {
            this.selectedRouteIds.push(id);
          }
        });
      }
    },

    getSelectedTrails() {
      return this.sortedTrails.filter((trail) =>
        this.selectedRouteIds.includes(this.getTrailId(trail))
      );
    },

    importSelected() {
      if (this.selectedRouteIds.length === 0) return;

      const selectedTrails = this.getSelectedTrails();
      if (this.callback) {
        this.callback(selectedTrails);
      }
    },

    toggleSelectAll() {
      if (this.allSelected) {
        // Deselect all
        this.selectedRouteIds = [];
      } else {
        // Select all
        this.selectedRouteIds = this.sortedTrails.map((trail) => this.getTrailId(trail));
      }
    },
  },
});
</script>

<style scoped>
.route-selector-container {
  color: #fff;
  max-height: 60vh;
  display: flex;
  flex-direction: column;
}

.selector-header {
  padding: 16px;
}

.selector-header p {
  color: rgba(255, 255, 255, 0.7);
  font-size: 14px;
}

.select-all-btn {
  font-size: 12px;
  padding: 4px 12px;
  border-color: rgba(255, 255, 255, 0.3);
  color: rgba(255, 255, 255, 0.9);
}

.select-all-btn:hover {
  background-color: rgba(255, 255, 255, 0.1);
  border-color: rgba(255, 255, 255, 0.5);
  color: #fff;
}

.trail-list {
  flex: 1;
  overflow-y: auto;
  padding: 8px 0;
}

.day-section {
  background: rgba(40, 42, 50, 0.95);
  margin: 0 1em 1.5em;
  /* padding: 1em; */
  border: 1px solid #4a4b51;
  border-radius: 14px;
  overflow: hidden;
}

.day-section:last-child {
  margin-bottom: 0;
}

.day-header {
  font-size: 12px;
  font-weight: 600;
  text-transform: uppercase;
  color: rgba(255, 255, 255, 0.5);
  padding: 8px 16px;
  margin: 0;
  background: rgba(255, 255, 255, 0.05);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  display: flex;
  align-items: center;
  cursor: pointer;
}

.day-header:hover {
  background: rgba(255, 255, 255, 0.08);
}

.day-checkbox {
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  background-color: transparent;
  position: relative;
  transition: all 0.2s ease;
}

.day-checkbox:hover {
  border-color: rgba(255, 255, 255, 0.5);
}

.day-checkbox:checked {
  background-color: #FFDF46;
  border-color: #FFDF46;
}

.day-checkbox:checked::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 2px;
  width: 4px;
  height: 8px;
  border: solid #000;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.day-checkbox:indeterminate {
  background-color: rgba(255, 223, 70, 0.3);
  border-color: #FFDF46;
}

.day-checkbox:indeterminate::after {
  content: '';
  position: absolute;
  left: 3px;
  top: 7px;
  width: 8px;
  height: 2px;
  background-color: #FFDF46;
}

.day-label {
  flex: 1;
  cursor: pointer;
  margin: 0;
}

.day-title {
  display: block;
}

.day-description {
  display: block;
  font-size: 11px;
  font-weight: normal;
  text-transform: none;
  color: rgba(255, 255, 255, 0.4);
  margin-top: 2px;
  font-style: italic;
}

.trail-item {
  display: flex;
  align-items: center;
  padding: 12px 16px;
  cursor: pointer;
  transition: background-color 0.2s;
  border-bottom: 1px solid rgba(255, 255, 255, 0.05);
  position: relative;
}

.trail-item:hover {
  background-color: rgba(255, 255, 255, 0.05);
}

.trail-item.selected {
  background-color: rgba(255, 223, 70, 0.15);
  border-left: 3px solid #FFDF46;
}

.trail-checkbox {
  margin-top: 0;
  cursor: pointer;
  flex-shrink: 0;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  width: 18px;
  height: 18px;
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: 4px;
  background-color: transparent;
  position: relative;
  transition: all 0.2s ease;
}

.trail-checkbox:hover {
  border-color: rgba(255, 255, 255, 0.5);
}

.trail-checkbox:checked {
  background-color: #FFDF46;
  border-color: #FFDF46;
}

.trail-checkbox:checked::after {
  content: '';
  position: absolute;
  left: 5px;
  top: 2px;
  width: 4px;
  height: 8px;
  border: solid #000;
  border-width: 0 2px 2px 0;
  transform: rotate(45deg);
}

.trail-number {
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.1);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  margin-right: 12px;
  flex-shrink: 0;
}

.trail-item.selected .trail-number {
  background: #FFDF46;
  color: #000;
}

.trail-info {
  flex: 1;
  min-width: 0;
  cursor: pointer;
  margin: 0;
}

.trail-title {
  margin: 0 0 4px 0;
  font-size: 14px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.trail-stats {
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  align-items: center;
  gap: 4px;
}

.separator {
  color: rgba(255, 255, 255, 0.3);
}

.panel-footer {
  padding: 12px 16px;
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  background: rgba(30, 32, 38, 0.8);
}

.total-stats {
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.total-label {
  font-size: 14px;
  color: rgba(255, 255, 255, 0.7);
}

.total-values {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 14px;
}

.total-values strong {
  font-weight: 600;
  color: #fff;
}

/* Scrollbar styling */
.trail-list::-webkit-scrollbar {
  width: 6px;
}

.trail-list::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.05);
}

.trail-list::-webkit-scrollbar-thumb {
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
}

.trail-list::-webkit-scrollbar-thumb:hover {
  background: rgba(255, 255, 255, 0.3);
}

/* Override Bootstrap modal styles */
#route-selector-dialog .modal-header {
  background: rgba(30, 32, 38, 0.95);
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  color: #fff;
}

#route-selector-dialog .modal-title {
  color: #fff;
}

#route-selector-dialog .modal-footer {
  background: rgba(30, 32, 38, 0.95);
  border-top: 1px solid rgba(255, 255, 255, 0.1);
}
</style>
